import { IS_TEACHER } from '@constants';
import { TABLE_STATUSES, WORD_STATUSES } from '@constants/gameConfigs';
import { useGameContext } from 'context/GameContext';
import { useStudentInGameInfo, useTeacherInGameInfo } from 'hooks';
import React, { memo, useCallback, useMemo } from 'react';
import trans from 'translation';
import { KButton, KColors, KContainer, KLabel } from 'uikit';

import InGameImage from '../InGame.Image';

interface IProps {
  status?: TABLE_STATUSES;
}

const WaitingForStart = ({ status }: IProps) => {
  const { onStartWord, isTaboo, isJumble, isHotseat, roomInfo } =
    useGameContext();

  const { teams = [], users = [] } = roomInfo || {};

  const isWaiting = status === TABLE_STATUSES.WAITING;

  const { runningTeam } = useTeacherInGameInfo();
  const { opposingTeam } = useStudentInGameInfo();

  const curTeam = IS_TEACHER ? runningTeam : opposingTeam;

  const wordIndex = useMemo(() => {
    if (curTeam) {
      const words = curTeam.words ?? [];
      const index = words.findIndex(
        w => w.status === WORD_STATUSES.NOT_STARTED_YET
      );
      if (index !== -1) {
        return index + 1;
      }
    }
    return 1;
  }, [curTeam]);

  const title = useMemo(() => {
    if (!isWaiting) {
      return trans('common.waiting_for_students');
    }

    if (isJumble) {
      return trans('common.start_game');
    }

    return (
      `${trans('common.start_word') + ` ${wordIndex}`}` +
      (!isTaboo ? '' : ` - ${curTeam?.name ?? ''}`)
    );
  }, [curTeam?.name, isJumble, isTaboo, isWaiting, wordIndex]);

  const renderHotseatContent = useCallback(() => {
    return (
      <KContainer.View marginT="1.5rem" dp="flex" gap="0.5rem">
        {teams
          .map(t => {
            const describerId = t.words?.[wordIndex - 1]?.describerId;
            const user = users.find(u => u.id === describerId);

            if (user) {
              return (
                <KLabel.Paragraph
                  typo="Text3xLgMedium"
                  color={KColors.white}
                  textAlign="center"
                  key={t.id}
                >
                  {t.name} {trans('common.guesser')} - {user.username}
                </KLabel.Paragraph>
              );
            }

            return null;
          })
          .filter(Boolean)}
      </KContainer.View>
    );
  }, [teams, users, wordIndex]);

  return (
    <KContainer.View alignItems padding="1.5rem">
      <InGameImage
        uri="waiting-for-start.webp"
        background={KColors.primary.normal}
        justifyContent={isHotseat ? 'center' : undefined}
      >
        {isHotseat && renderHotseatContent()}

        <KButton.Solid
          marginT="3rem"
          title={title}
          kind="gray"
          background={KColors.white}
          textColor={KColors.primary.normal}
          onPress={onStartWord}
          disabled={!isWaiting}
        />
      </InGameImage>
    </KContainer.View>
  );
};

WaitingForStart.displayName = 'Containers.InGame.WaitingForStart';

export default memo(WaitingForStart);
